import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Question } from '../../../interfaces/question.interface';
import { AssessmentSurvey } from '../../../interfaces/assessment.interface';
import { createComment } from '../../../services/commentsLogs';
import { createAssessorAnswer, updateAssessorAnswerComments } from '../../../services/assessments';
import { setAssessmentQuestionStatus } from '../../../app/store/questionnaireAnswerSlice';

// Click-driven comment post (never from useEffect). Comments hang off the AssessorAnswer, so a question with no finding yet gets a bare record first — the engine's path.
export function usePostComment() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.user);
  const { assessment } = useAppSelector(state => state.assessments);
  const { survey } = useAppSelector(state => state.surveys);
  const { assessmentQuestionsStatus } = useAppSelector(state => state.questionnaireAnswer);

  const postComment = useCallback(async (question: Question, text: string, attachments: number[]): Promise<void> => {
    if (!user || !assessment || !survey) throw new Error('Missing assessment context');
    const existing = assessmentQuestionsStatus?.[question.id];
    const comment = await createComment({ user, text, attachments });
    let answerId = existing?.id;
    if (answerId) {
      const existingIds = (existing?.comments || []).map(c => (typeof c === 'number' ? c : c.id!));
      await updateAssessorAnswerComments(answerId, [...existingIds, comment.id!]);
    } else {
      const assessmentSurveyId = (assessment.surveys as AssessmentSurvey[]).find(assSur =>
        (typeof assSur.survey === 'number' ? assSur.survey === survey.id : assSur.survey.id === survey.id))?.id;
      if (!assessmentSurveyId) throw new Error('Assessment survey not found');
      const created = await createAssessorAnswer({ question: question.id, assessmentSurvey: assessmentSurveyId, comments: [comment.id!] });
      answerId = created.id;
    }
    // Appends to the question's comments and seeds the dictionary in one go, so list and count update without reload.
    dispatch(setAssessmentQuestionStatus({ question, id: answerId, status: existing?.status || 'incomplete', comment }));
  }, [dispatch, user, assessment, survey, assessmentQuestionsStatus]);

  return { postComment };
}
